import "@/styles/globals.css"; 
import type { AppProps } from "next/app";
import AuthInitializer from "@/components/AuthInitializer";
import AuthStatusWidget from "@/components/AuthStatusWidget";
import CartDebugWidget from "@/components/CartDebugWidget";
import OneTapLogin from "@/components/OneTapLogin";
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import posthog from 'posthog-js';
import Script from 'next/script';
import { 
  hasConsent, 
  subscribeToConsentChanges, 
  hasUserMadeChoice 
} from '@/utils/cookieConsentUtil';
import { initializeGTMConsent } from '@/utils/gtmConsent';

// Type definition for cookie preferences
interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  advertising: boolean;
  [key: string]: boolean; // For possible additional cookie types
}

export default function App({ Component, pageProps }: AppProps) {
  const router = useRouter();
  const [analyticsEnabled, setAnalyticsEnabled] = useState<boolean>(false);
  const [posthogInitialized, setPosthogInitialized] = useState<boolean>(false);
  const [gtmLoaded, setGtmLoaded] = useState<boolean>(false);

  useEffect(() => {
    // Инициализация Google Consent Mode
    initializeGTMConsent();
    
    // Only check consent after we're sure we're in the browser
    if (typeof window === 'undefined') return;

    // Check if the user has analytics consent
    const currentAnalyticsEnabled = hasConsent('analytics');
    setAnalyticsEnabled(currentAnalyticsEnabled);
    
    // We ONLY initialize PostHog if the user has explicitly given consent
    // This means they must have made a choice (hasUserMadeChoice) AND enabled analytics
    if (currentAnalyticsEnabled && hasUserMadeChoice()) {
      initializePosthog();
    }
    
    // Subscribe to consent changes to handle enabling/disabling analytics
    const unsubscribe = subscribeToConsentChanges((newPreferences: CookiePreferences) => {
      const newAnalyticsEnabled = newPreferences.analytics;
      setAnalyticsEnabled(newAnalyticsEnabled);
      
      // Handle analytics state change
      if (newAnalyticsEnabled && !analyticsEnabled) {
        // Analytics was just enabled
        initializePosthog();
      } else if (!newAnalyticsEnabled && analyticsEnabled && posthogInitialized) {
        // Analytics was just disabled
        posthog.opt_out_capturing();
      }
    });
    
    return () => {
      unsubscribe();
    };
  }, [analyticsEnabled]);

  // Separate function to initialize PostHog
  const initializePosthog = () => {
    if (posthogInitialized) {
      // If already initialized, just opt back in
      posthog.opt_in_capturing();
    } else {
      // First-time initialization
      posthog.init('phc_dfLSIQAGgA350UPP9HEKtSkiWIRc2E3HCIUVSs44zWo', {
        api_host: 'https://eu.i.posthog.com',
        person_profiles: 'identified_only',
        capture_pageview: false, // Disable automatic pageview capture
      });
      setPosthogInitialized(true);
    }
    
    // Manually capture current pageview
    posthog.capture('$pageview');
  };

  useEffect(() => {
    // Track route changes, but only if analytics is enabled
    const handleRouteChange = () => {
      if (analyticsEnabled && posthogInitialized) {
        posthog.capture('$pageview');
      }
    };
    
    router.events.on('routeChangeComplete', handleRouteChange);
    
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events, analyticsEnabled, posthogInitialized]);

  const handleGTMLoad = () => {
    setGtmLoaded(true);
  };

  return (
    <>
      {/* Google Tag Manager - загружаем всегда независимо от настройки аналитики */}
      <Script 
        id="google-tag-manager" 
        strategy="afterInteractive"
        onLoad={handleGTMLoad}
      >
        {`
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','GTM-KC5G5WLD');
        `}
      </Script>

      {/* noscript tag для GTM */}
      <noscript>
        <iframe
          src="https://www.googletagmanager.com/ns.html?id=GTM-KC5G5WLD"
          height="0"
          width="0"
          className="hidden invisible"
        ></iframe>
      </noscript>

      <AuthInitializer />
      <Component {...pageProps} />
      {process.env.NODE_ENV === 'development' && <AuthStatusWidget />}
    </>
  );
}